// Enabling and disabling push on this device.
//
// The subtleties that make web push actually work in practice:
//   · permission is requested from a user gesture, never on load — a prompt shown
//     unprompted gets denied, and a denial is close to permanent
//   · the existing subscription is reused when there is one, rather than churning
//     the endpoint on every visit
//   · the service worker re-registers itself on `pushsubscriptionchange` (see sw.js),
//     so a rotated endpoint heals without the user doing anything

import { api } from './api.ts';

export type PushState = 'unsupported' | 'unconfigured' | 'denied' | 'off' | 'on';

/**
 * VAPID public key (base64url) → the byte array `pushManager.subscribe` wants.
 *
 * Backed by an explicit ArrayBuffer so the result is a plain `Uint8Array<ArrayBuffer>`
 * — `applicationServerKey` will not accept the SharedArrayBuffer-capable default.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  const { configured } = await api.push.key().catch(() => ({ configured: false }));
  if (!configured) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  return existing ? 'on' : 'off';
}

/** Subscribes this device. Must be called from a user gesture. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  }

  const { key } = await api.push.key();
  if (!key) return 'unconfigured';

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription()
    ?? await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });

  await api.push.subscribe(subscription.toJSON());
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    // Tell the server first: if unsubscribing locally succeeds but the call fails,
    // the server keeps pushing to a dead endpoint until it is pruned by a 410.
    await api.push.unsubscribe(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  }
  return 'off';
}

/**
 * Registers the service worker.
 *
 * Deliberately not awaited by the UI — a slow registration must never delay first
 * paint, and the app is fully usable without push.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((e) => {
      console.warn('Service worker registration failed', e);
    });
  });
}

/**
 * Handles a deep link posted by the service worker when a notification is tapped.
 *
 * Focusing the window alone is not enough — without this the app opens on whatever
 * screen it was last on, and every notification lands in the wrong place.
 */
export function listenForNotificationNavigation(navigate: (path: string) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined;

  const onMessage = (event: MessageEvent): void => {
    const data = event.data as { type?: string; url?: string } | null;
    if (data?.type === 'healthlab-open' && typeof data.url === 'string') {
      navigate(data.url);
    }
  };

  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
}
